// Cross-driver colormap orchestration. When the colormap, its range, or any
// dependent setting changes, this module re-pushes the new colormap into
// every consumer: slice textures, the volume shader's uniforms, the
// contour mesh tint, and the colorbar HUD. UI dispatches a binding-scoped
// intent (`colormap:update`) — or a legacy global one — and the handler
// in @app/intents/colormap calls syncColormap() here.
//
// Stage 2 makes the fan-out per-binding. Each primitive resolves its own
// `*ColormapId` to a `ColormapBinding`; render updates take binding state
// instead of legacy global params. The legacy `params.colormap*` fields
// still mirror the active binding (translation layer) so legacy callers
// keep working until Task 4 migrates them.
//
// Lives in @app because it touches @scene mutators; @ui must not import
// scene modules directly.

import * as THREE from 'three';
import { updateSliceData } from '@scene/slices';
import { updateVolumeColormap } from '@scene/volume';
import {
    colorOptsFromBinding,
    strengthToColorFromBinding,
} from '@physics/colormap';
import { debugWarn } from '@adapters/debug';
import type { ViewerContext } from './context';
import type { ColormapBinding, ParamsState } from '@store/schema';

/**
 * Resolve a primitive's binding, falling back to a synthetic binding built
 * from the legacy global params so first-load paint (before
 * applyComponentColormapDefaults runs) still produces something sensible.
 */
function bindingFor(primitive: 'slices' | 'volume' | 'contour' | 'fieldlines', params: ParamsState): ColormapBinding {
    const key = primitive === 'slices' ? 'slicesColormapId'
        : primitive === 'volume' ? 'volumeColormapId'
        : primitive === 'contour' ? 'contourColormapId'
        : 'fieldlinesColormapId';
    const id = params[key];
    const b = id ? params.bindings[id] : null;
    if (b) return b;
    return legacyBinding(params);
}

function legacyBinding(params: ParamsState): ColormapBinding {
    return {
        id: '__legacy__',
        field: params.fieldComponent || 'magnitude',
        colormap: params.colormap,
        min: params.colormapMin,
        max: params.colormapMax,
        log: !!params.colormapLog,
        logFloor: 1e-3,
        units: params.colormapUnits,
        centerOnZero: !!params.centerOnZero,
        transparentBounds: !!params.transparentBounds,
    };
}

/**
 * Compatibility shim: legacy callers used to mount the colorbar here. The
 * colorbar stack driver (`@ui/colorbarStack`) now subscribes directly to
 * the binding registry and re-paints on every change, so explicit mount
 * calls are no-ops. Kept as an exported function so old bootstrap paths
 * don't error during the Stage 2 transition.
 */
export function mountColorbar(_viewer: ViewerContext, _params: ParamsState): void {
    // Intentional no-op — colorbarStack owns the colorbar render lifecycle.
}

export function syncColormap(viewer: ViewerContext, params: ParamsState): void {
    const sliceBinding = bindingFor('slices', params);
    const volumeBinding = bindingFor('volume', params);
    const contourBinding = bindingFor('contour', params);

    // --- slices ---
    const slices = viewer.objects.slices.state;
    const sliceOpts = colorOptsFromBinding(viewer, sliceBinding);
    if (slices.XY && slices.XY.mesh) updateSliceData('XY', viewer, sliceOpts, slices.XY, params.sliceZ);
    if (slices.ZY && slices.ZY.mesh) updateSliceData('ZY', viewer, sliceOpts, slices.ZY, params.sliceZY);
    if (slices.ZX && slices.ZX.mesh) updateSliceData('ZX', viewer, sliceOpts, slices.ZX, params.sliceZX);

    // --- volume ---
    const vol = viewer.objects.volume;
    const volMat = vol.material as THREE.ShaderMaterial | null;
    if (params.volumeEnabled && volMat && vol.scalarMin !== null && vol.scalarMax !== null) {
        try {
            updateVolumeColormap({
                THREE, volumeMaterial: volMat,
                volumeScalarMin: vol.scalarMin, volumeScalarMax: vol.scalarMax,
                fieldStrengthToColor: (s) => strengthToColorFromBinding(viewer, volumeBinding, s),
                colormapMin: volumeBinding.min, colormapMax: volumeBinding.max,
                transparentBounds: !!volumeBinding.transparentBounds,
            });
        } catch (e) {
            debugWarn(viewer, 'Failed updating volume colormap:', e);
        }
    }

    // --- colorbar HUD ---
    // Owned by the @ui/colorbarStack subscription — no direct call here.

    // --- contours ---
    const contours = viewer.objects.contours.meshes;
    if (contours.length) {
        const sampleVal = (typeof params.contourValue === 'number')
            ? params.contourValue
            : ((contourBinding.min + contourBinding.max) * 0.5);
        const c = strengthToColorFromBinding(viewer, contourBinding, sampleVal);
        const hex = (c && typeof c.r === 'number') ? ((c.r << 16) | (c.g << 8) | c.b) : 0xff66aa;
        const alpha = (c && typeof c.a === 'number') ? Math.max(0.2, Math.min(0.9, c.a / 255)) : 0.6;
        contours.forEach((m) => {
            if (!m || !m.material) return;
            const mat = Array.isArray(m.material) ? m.material[0] : m.material;
            if (!mat) return;
            const matWithColor = mat as THREE.MeshStandardMaterial;
            if (matWithColor.color && typeof matWithColor.color.setHex === 'function') matWithColor.color.setHex(hex);
            if (typeof mat.opacity === 'number') { mat.opacity = alpha; mat.transparent = alpha < 1.0; }
            mat.needsUpdate = true;
        });
    }
}
